import { FC, useEffect, useRef, useState } from 'react';
import './SportsbookContainer.scss';
import { useAuth } from 'react-oidc-context';
import { retrieveLoginToken } from './service';
import { Prompt } from '../shared/Prompt';
import PromptImage from '../../assets/Prompt/registration-abandon-prompt.png';
import { Paragraph } from '../shared/Paragraph';
import { useNavigate } from 'react-router-dom';
import { useSignIn } from '../../hooks/auth';
import { LogoSpinner } from '../shared/LogoSpinner/LogoSpinner';

interface SncBridge {
  init: (iframe: HTMLIFrameElement) => void;
  requestContent: (options: { contentId: string; destinationId: HTMLElement }) => void;
}

declare global {
  interface Window {
    SncBridge: SncBridge;
  }
}

export const SportsbookContainer: FC = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [sncBridgeReady, setSncBridgeReady] = useState(false);
  const auth = useAuth();
  const authenticated = auth.isAuthenticated;
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const navigate = useNavigate();
  const signIn = useSignIn();

  useEffect(() => {
    const loadSncBridge = (): void => {
      if (window.SncBridge) {
        setSncBridgeReady(true);

        return;
      }

      const script = document.createElement('script');

      script.type = 'module';
      script.textContent = `
        import { SncBridge } from '${process.env.REACT_APP_SPORTX_URL}/lib/snc-bridge.js';
        window.SncBridge = SncBridge;
        window.dispatchEvent(new CustomEvent('sncBridgeLoaded'));
      `;

      const handleSncBridgeLoaded = (): void => {
        setSncBridgeReady(true);
        window.removeEventListener('sncBridgeLoaded', handleSncBridgeLoaded);
      };

      window.addEventListener('sncBridgeLoaded', handleSncBridgeLoaded);
      document.head.appendChild(script);
    };

    loadSncBridge();
  }, []);

  useEffect(() => {
    if (iframeReady && sncBridgeReady && iframeRef.current && window.SncBridge) {
      const onSncBridgeReady = (): void => {
        if (mobileMenuRef.current) {
          window.SncBridge.requestContent({
            contentId: 'tab-bar',
            destinationId: mobileMenuRef.current,
          });
        }
      };

      try {
        window.SncBridge.init(iframeRef.current);
        onSncBridgeReady();
      } catch (error) {
        console.error('sb: Failed to initialize SncBridge:', error);
      }
    }
  }, [iframeReady, sncBridgeReady]);

  const handleMessage = (event: MessageEvent): void => {
    if (event.data.event === 'sportnco_iframe_ready') {
      setIframeReady(true);
    }

    if (event.data.event === 'sportnco_window_resize') {
      const iframe = iframeRef.current;
      const height = event.data.height;

      if (iframe) iframe.style.height = height + 'px';
    }

    if (event.data.event === 'sportbook_request_parent_scroll_top') {
      window.scroll({
        top: 0,
        behavior: 'smooth',
      });
    }

    if (event.data.event === 'request_login_open') setShowPrompt(true);
  };

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    const loginToSportsbook = async (): Promise<void> => {
      if (authenticated && iframeReady && iframeRef.current && iframeRef.current.contentWindow) {
        try {
          const resp = await retrieveLoginToken(auth.user?.profile.user_profile_id as string);
          const message = { method: 'LOGIN', args: { sessionId: resp.sessionContextId } };

          iframeRef.current.contentWindow.postMessage(message, process.env.REACT_APP_SPORTX_URL as string);
        } catch (error) {
          console.error('sb: Failed to retrieve login token:', error);
        }
      }
    };

    if (!authenticated && iframeRef.current && iframeRef.current.contentWindow)
      iframeRef.current.contentWindow.postMessage({ method: 'LOGOUT' }, '*');

    loginToSportsbook();
  }, [authenticated, iframeReady]);

  return (
    <>
      {!iframeReady && <LogoSpinner className="sb-spinner" size="large" />}
      <iframe
        ref={iframeRef}
        className={`sb-iframe ${iframeReady ? 'visible' : ''}`}
        title={'sportsbook'}
        src={(process.env.REACT_APP_SPORTX_URL as string) + '/en'}
      />
      <div ref={mobileMenuRef} id="mobile-tab-bar" className="mobile-tab-bar-container" />
      <Prompt
        show={showPrompt}
        PrimaryButton={{ text: 'REGISTER', buttonVariant: 'primary', onClick: () => navigate('/registration') }}
        SecondaryButton={{ text: 'LOGIN', buttonVariant: 'secondary', onClick: signIn }}
        image={{ src: PromptImage }}
        onClose={(): void => setShowPrompt(false)}
      >
        <Paragraph size="md">You must be logged in to do that. Would you like to log in or sign up now?</Paragraph>
      </Prompt>
    </>
  );
};
